import { EditorContent, useEditor } from '@tiptap/react';
import { baseEditorClasses, codeEditorClasses, lowlight } from './editorConfig';
import { CustomMention, getMentionConfig } from './extensions/CustomMention';
import { createMentionSuggestion } from './mentionSuggestion';
import { S3Image } from './extensions/S3Image';
import EditorBubbleMenu from './EditorBubbleMenu';
import StarterKit from '@tiptap/starter-kit';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Gapcursor from '@tiptap/extension-gapcursor';
import { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/context/AuthContext';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { ChevronDown } from 'lucide-react';

// Syntax highlighting
import '../../syntax-highlighting.css';
import Link from '@tiptap/extension-link';
import { S3File } from './extensions/S3File';

// Helper to get image dimensions from a file or blob URL
const getImageDimensions = (src) => {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
        };
        img.onerror = () => {
            resolve({ width: null, height: null });
        };
        img.src = src;
    });
};

const TiptapEditor = forwardRef(function TiptapEditor({
    // Basic configuration
    content = '',
    placeholder = '',
    editable = true,
    className = '',

    // Editor features
    withCodeBlocks = true,
    withMentions = true,
    withS3Images = true,
    withS3Uploads = false,
    withBubbleMenu = true,

    // Handlers
    onUpdate = () => { },
    onKeyDown,
    onEditorReady,

    organizationSlug,
    taskKey,
    onTaskChange,
    onFileUpload,

    // Custom styling
    customEditorClasses,
}, ref) {
    const { axiosInstance } = useAuth();
    const [isUploading, setIsUploading] = useState(false);
    const { toast } = useToast();

    const handleFileUpload = async (files, insertIntoEditor = false, position = null) => {
        // Only proceed if withS3Uploads is enabled
        if (!withS3Uploads) {
            toast({
                description: "File uploads are not enabled",
                variant: "destructive"
            });
            return;
        }

        const fileArray = Array.isArray(files) ? files : [files];
        if (fileArray.length === 0) return;

        setIsUploading(true);

        try {
            const docSize = editor?.state?.doc?.content?.size || 0;

            const filePlaceholders = [];
            let insertPosition = position !== null ? Math.min(position, docSize) : null;

            if (insertPosition === null && insertIntoEditor && fileArray.length > 1) {
                const { selection } = editor.state;
                insertPosition = Math.min(selection.from, docSize);
            }

            for (let i = 0; i < fileArray.length; i++) {
                const file = fileArray[i];

                const timestamp = Date.now();
                const randomString = Math.random().toString(36).substring(2, 15);
                const fileIdentifier = `${file.name.substring(0, 10)}_${file.size}`.replace(/[^a-zA-Z0-9]/g, '');
                
                const uploadId = [
                    organizationSlug || 'no-org',
                    taskKey || 'no-task',
                    timestamp,
                    randomString,
                    fileIdentifier
                ].join('_');
                const tempKey = `temp_${timestamp}_${randomString}_${fileIdentifier}`.slice(0, 120);

                if (insertIntoEditor) {
                    const placeholderUrl = URL.createObjectURL(file);
                    
                    // Get image dimensions for skeleton placeholders (prevents layout shift)
                    let imageDimensions = { width: null, height: null };
                    if (file.type.startsWith('image/')) {
                        imageDimensions = await getImageDimensions(placeholderUrl);
                    }

                    if (insertPosition !== null) {
                        const currentDocSize = editor?.state?.doc?.content?.size || 0;
                        const safePosition = Math.min(insertPosition, currentDocSize);


                        if (file.type.startsWith('image/')) {
                            editor.chain().insertContentAt(safePosition, {
                                type: 's3Image',
                                attrs: {
                                    src: placeholderUrl,
                                    key: tempKey,
                                    uploadId,
                                    width: imageDimensions.width,
                                    height: imageDimensions.height,
                                }
                            }).run();
                        } else {
                            editor.chain().insertContentAt(safePosition, {
                                type: 's3File',
                                attrs: {
                                    src: placeholderUrl,
                                    key: tempKey,
                                    filename: file.name,
                                    contentType: file.type,
                                    fileSize: file.size,
                                    uploadId,
                                }
                            }).run();
                        }

                        if (i < fileArray.length - 1) {
                            const newDocSize = editor?.state?.doc?.content?.size || 0;
                            const paragraphPosition = Math.min(safePosition + 1, newDocSize);

                            editor.chain().insertContentAt(paragraphPosition, {
                                type: 'paragraph'
                            }).run();

                            // Update insertPosition for next file: current safe position + file + paragraph
                            insertPosition = safePosition + 2;
                        } else {
                            // For the last file, just move position forward by 1
                            insertPosition = safePosition + 1;
                        }
                    } else {
                        // Single file cursor-based insertion
                        if (file.type.startsWith('image/')) {
                            editor.chain().focus().setS3Image({
                                src: placeholderUrl,
                                key: tempKey,
                                uploadId,
                                width: imageDimensions.width,
                                height: imageDimensions.height,
                            }).run();
                        } else {
                            editor.chain().focus().setS3File({
                                src: placeholderUrl,
                                key: tempKey,
                                filename: file.name,
                                contentType: file.type,
                                fileSize: file.size,
                                uploadId,
                            }).run();
                        }
                    }

                    filePlaceholders.push({ file, placeholderUrl, uploadId, tempKey, imageDimensions });
                } else {
                    filePlaceholders.push({ file, placeholderUrl: null, uploadId, tempKey, imageDimensions: null });
                }
            }

            const uploadPromises = filePlaceholders.map(async ({ file, placeholderUrl, uploadId, tempKey }) => {
                try {
                    const formData = new FormData();
                    formData.append('file', file);

                    let response;
                    if (taskKey && organizationSlug) {
                        const queryParams = new URLSearchParams();
                        queryParams.set('uploadId', uploadId);
                        queryParams.set('tempKey', tempKey);
                        response = await axiosInstance.post(`/tasks/${organizationSlug}/${taskKey}/attachments?${queryParams.toString()}`, formData);
                    } else {
                        response = await onFileUpload(file, uploadId, tempKey);
                    }

                    if (placeholderUrl && uploadId) {
                        let matchingAttachment = response.data.attachments.find(att => att.uploadId === uploadId);

                        if (!matchingAttachment) {
                            const matchingAttachments = response.data.attachments.filter(att =>
                                att.filename === file.name && att.contentType === file.type
                            );
                            matchingAttachment = matchingAttachments[matchingAttachments.length - 1];
                        }

                        if (!matchingAttachment) {
                            console.warn(`No matching attachment found for uploadId: ${uploadId}, filename: ${file.name}`);
                            matchingAttachment = response.data.attachments[response.data.attachments.length - 1];
                        }

                        const { url, filename, key } = matchingAttachment;

                        const { state } = editor;
                        const { doc } = state;
                        let replacementMade = false;

                        doc.descendants((node, pos) => {
                            if (replacementMade) return false;

                            if (file.type.startsWith('image/')) {
                                if (node.type.name === 's3Image' && node.attrs.uploadId === uploadId) {
                                    editor.chain()
                                        .setNodeSelection(pos)
                                        .updateS3Image({
                                            src: url,
                                            key: key,
                                            alt: filename,
                                            uploadId: null,
                                        })
                                        .run();
                                    URL.revokeObjectURL(placeholderUrl);
                                    replacementMade = true;
                                }
                            } else {
                                if (node.type.name === 's3File' && node.attrs.uploadId === uploadId) {
                                    editor.chain()
                                        .setNodeSelection(pos)
                                        .updateS3File({
                                            src: url,
                                            key: key,
                                            filename: filename,
                                            uploadId: null,
                                        })
                                        .run();
                                    URL.revokeObjectURL(placeholderUrl);
                                    replacementMade = true;
                                }
                            }
                        });

                        if (!replacementMade) {
                            console.warn(`Failed to find placeholder ${uploadId} for file ${file.name}`);
                        }
                    }

                    return { response, file, uploadId };
                } catch (error) {
                    console.error(`Error uploading file ${file.name}:`, error);

                    if (placeholderUrl) {
                        URL.revokeObjectURL(placeholderUrl);
                    }

                    return { error, file, uploadId };
                }
            });

            const results = await Promise.all(uploadPromises);
            const succeeded = results.filter(r => r.response);
            const failed = results.filter(r => r.error);

            const latestSuccess = succeeded[succeeded.length - 1];
            if (latestSuccess?.response) {
                const serverFailed = latestSuccess.response.data?.failedUploads || [];
                if (serverFailed.length > 0) {
                    failed.push(...serverFailed.map(f => ({ file: { name: f.filename } })));
                }

                if (onTaskChange) {
                    onTaskChange(latestSuccess.response.data);
                }
            }

            // Remove placeholder nodes from editor for failed uploads
            if (failed.length > 0) {
                const { state } = editor;
                const { doc, tr } = state;
                const nodesToRemove = [];

                doc.descendants((node, pos) => {
                    if ((node.type.name === 's3Image' || node.type.name === 's3File') && node.attrs.uploadId) {
                        const isFailed = failed.some(f => f.uploadId === node.attrs.uploadId);
                        if (isFailed) {
                            nodesToRemove.push({ pos, size: node.nodeSize });
                        }
                    }
                });

                // Remove in reverse order to keep positions valid
                for (let i = nodesToRemove.length - 1; i >= 0; i--) {
                    const { pos, size } = nodesToRemove[i];
                    tr.delete(pos, pos + size);
                }

                if (nodesToRemove.length > 0) {
                    editor.view.dispatch(tr);
                }
            }

            if (onUpdate) {
                onUpdate({ editor });
            }

            if (failed.length > 0 && succeeded.length > 0) {
                toast({
                    description: (
                        <Collapsible>
                            <CollapsibleTrigger className="flex items-center gap-1 font-medium cursor-pointer">
                                {succeeded.length} uploaded, {failed.length} failed
                                <ChevronDown className="h-3 w-3" />
                            </CollapsibleTrigger>
                            <CollapsibleContent className="mt-1 text-xs opacity-80">
                                {failed.map((f, i) => <div key={i}>{f.file.name}</div>)}
                            </CollapsibleContent>
                        </Collapsible>
                    ),
                });
            } else if (failed.length > 0) {
                toast({
                    description: (
                        <Collapsible>
                            <CollapsibleTrigger className="flex items-center gap-1 font-medium cursor-pointer">
                                Failed to upload {failed.length} file{failed.length > 1 ? 's' : ''}
                                <ChevronDown className="h-3 w-3" />
                            </CollapsibleTrigger>
                            <CollapsibleContent className="mt-1 text-xs opacity-80">
                                {failed.map((f, i) => <div key={i}>{f.file.name}</div>)}
                            </CollapsibleContent>
                        </Collapsible>
                    ),
                });
            } else {
                const fileCount = fileArray.length;
                toast({
                    description: `${fileCount} file${fileCount > 1 ? 's' : ''} uploaded successfully`
                });
            }
        } catch (error) {
            console.error('Error uploading files:', error);
            toast({
                description: "Failed to upload files",
                variant: "destructive"
            });
        } finally {
            setIsUploading(false);
        }
    };

    const editor = useEditor({
        extensions: [
            Link.extend({ inclusive: false }).configure({
                defaultProtocol: 'https',
                HTMLAttributes: {
                    class: 'text-violet-600 dark:text-violet-400 cursor-pointer',
                },
                openOnClick: false,
            }),
            StarterKit.configure({
                codeBlock: !withCodeBlocks,
            }),
            withCodeBlocks && CodeBlockLowlight.configure({
                lowlight,
                HTMLAttributes: {
                    class: 'hljs'
                },
            }),
            Underline,
            TextAlign.configure({
                types: ['heading', 'paragraph', 's3Image'],
            }),
            Placeholder.configure({
                placeholder,
                emptyEditorClass: "before:content-[attr(data-placeholder)] before:float-left before:text-neutral-500 dark:before:text-neutral-600 before:h-0 before:pointer-events-none",
            }),
            withMentions && CustomMention.configure({
                ...getMentionConfig(axiosInstance, organizationSlug),
                suggestion: withMentions && axiosInstance && organizationSlug ?
                    createMentionSuggestion(axiosInstance, organizationSlug) :
                    undefined,
            }),
            withS3Images && S3Image,
            withS3Images && S3File,
            Gapcursor,
        ].filter(Boolean),

        editorProps: {
            attributes: {
                class: customEditorClasses || `${baseEditorClasses} ${codeEditorClasses} ${className}`,
            },
            handlePaste: (view, event) => {
                if (!withS3Uploads) return false;

                const items = Array.from(event.clipboardData?.items || []);
                const fileItems = items.filter(item => item.type.indexOf('/') !== -1);

                if (fileItems.length > 0) {
                    event.preventDefault();
                    const files = fileItems.map(item => item.getAsFile()).filter(Boolean);
                    if (files.length > 0) {
                        setIsUploading(true);
                        handleFileUpload(files, true);
                    }
                }
                return false;
            },
            handleDrop: (view, event, slice, moved) => {
                // Handle existing attachment drops
                if (withS3Uploads && !moved && event.dataTransfer?.getData('application/json')) {
                    try {
                        const dropData = JSON.parse(event.dataTransfer.getData('application/json'));
                        
                        if (dropData.type === 'attachment') {
                            event.preventDefault();
                            const coordinates = view.posAtCoords({
                                left: event.clientX,
                                top: event.clientY,
                            });

                            if (coordinates) {
                                const { attachment, attachmentType } = dropData;
                                
                                if (attachmentType === 'image') {
                                    // Fetch dimensions before inserting to ensure correct aspect ratio
                                    getImageDimensions(attachment.url).then(dimensions => {
                                        // Insert image attachment with dimensions
                                        editor.chain().focus().insertContentAt(coordinates.pos, {
                                            type: 's3Image',
                                            attrs: {
                                                src: attachment.url,
                                                key: attachment.key,
                                                alt: attachment.alt || attachment.filename,
                                                size: 'full',
                                                textAlign: 'left',
                                                width: dimensions.width,
                                                height: dimensions.height
                                            }
                                        }).run();
                                    });
                                } else {
                                    // Insert file attachment
                                    editor.chain().focus().insertContentAt(coordinates.pos, {
                                        type: 's3File',
                                        attrs: {
                                            src: attachment.url,
                                            key: attachment.key,
                                            filename: attachment.filename,
                                            contentType: attachment.contentType,
                                            fileSize: attachment.fileSize
                                        }
                                    }).run();
                                }
                            }
                            return true;
                        }
                    } catch (error) {
                        console.error('Error parsing drop data:', error);
                    }
                }

                // Handle file uploads
                if (withS3Uploads && !moved && event.dataTransfer?.files?.length) {
                    const files = Array.from(event.dataTransfer.files);
                    setIsUploading(true);
                    event.preventDefault();
                    const coordinates = view.posAtCoords({
                        left: event.clientX,
                        top: event.clientY,
                    });

                    if (coordinates) {
                        handleFileUpload(files, true, coordinates.pos);
                    }
                    return true;
                }
                return false;
            },
            handleKeyDown: onKeyDown,
        },

        content,
        editable: !isUploading && editable,
        onUpdate: ({ editor }) => {
            // Check if any nodes are currently uploading
            let hasUploadingNodes = false;
            editor.state.doc.descendants(node => {
                if (node.type.name === 's3Image' && node.attrs.uploadId) {
                    hasUploadingNodes = true;
                }
            });

            if (!hasUploadingNodes) {
                onUpdate({ editor });
            }
        },
    });

    useEffect(() => {
        if (editor && onEditorReady) {
            onEditorReady(editor);
        }
    }, [editor, onEditorReady]);

    // Add this useImperativeHandle hook after the editor definition
    useImperativeHandle(ref, () => ({
        commands: editor?.commands,
        editor: editor
    }), [editor]);

    if (!editor) {
        return null;
    }

    return (
        <div className="relative">
            {withBubbleMenu && <EditorBubbleMenu editor={editor}/>}
            <EditorContent editor={editor} />
        </div>
    );
});

TiptapEditor.displayName = 'TiptapEditor';

export default TiptapEditor;
